import type { UserRole } from '@micarahr/shared-types';
import {
  primaryWebRole,
  webPermissionsFor,
  webPermissionsForRole,
  WEB_SUPPORTED_ROLES,
} from '@micarahr/permissions/web';
import type { SystemUser } from '@micarahr/shared-types';
import { readAll } from './collections';
import { errors, runtime, serviceCall } from './runtime';
import { preferences } from './storage';
import { recordAudit } from './audit';
import { DEMO_CREDENTIALS, DEMO_PASSWORD } from './demo-credentials';

/**
 * Authentication.
 *
 * A prototype session: no password hashing, no tokens, no server. The single
 * shared demonstration password is checked so the sign-in form is a real form
 * with real validation and real failure states — not a button that logs you in.
 *
 * The session is persisted in localStorage rather than IndexedDB because the
 * shell needs to know who is signed in before its first paint.
 */

const SESSION_KEY = 'session';

export interface Session {
  userId: string;
  employeeId: string | null;
  displayName: string;
  email: string;
  roles: UserRole[];
  primaryRole: UserRole;
  scopedDepartmentIds: string[];
  permissions: string[];
  signedInAt: string;
}

function toSession(user: SystemUser): Session {
  // `permissionsForRoles` only knows the platform PERMISSIONS map. The
  // web-only permissions — compensation, benefits, loans, cases, onboarding,
  // probation, offboarding, performance — live in WEB_ROLE_EXTRAS, and without
  // folding them in here a company administrator signs in unable to open ten of
  // their own modules.
  const extraPermissions = [...new Set(user.roles.flatMap((role) => webPermissionsForRole(role)))];

  const subject = {
    userId: user.id,
    roles: user.roles,
    extraPermissions,
    scopedDepartmentIds: user.scopedDepartmentIds,
    employeeId: user.employeeId,
  };
  return {
    userId: user.id,
    employeeId: user.employeeId,
    displayName: user.displayName,
    email: user.email,
    roles: user.roles,
    primaryRole: primaryWebRole(user.roles),
    scopedDepartmentIds: user.scopedDepartmentIds,
    permissions: [...webPermissionsFor(subject)],
    signedInAt: runtime.now(),
  };
}

export const authService = {
  /** Restore a session synchronously so the shell never flashes the sign-in page. */
  restore(): Session | null {
    return preferences.read<Session | null>(SESSION_KEY, null);
  },

  async signIn(email: string, password: string): Promise<Session> {
    return serviceCall('authService', async () => {
      const normalised = email.trim().toLowerCase();
      const user = readAll<SystemUser>('systemUsers').find(
        (candidate) => candidate.email.toLowerCase() === normalised,
      );

      if (!user || password !== DEMO_PASSWORD) {
        // Deliberately identical for an unknown address and a wrong password:
        // an error that distinguishes them is an account-enumeration oracle.
        throw errors.validation('That email address and password do not match an account.');
      }

      if (user.status === 'deactivated') {
        throw errors.validation('This account is inactive. Ask an administrator to reactivate it.');
      }
      if (user.status === 'locked') {
        throw errors.validation('This account is locked. Ask an administrator to unlock it.');
      }

      const usableRoles = user.roles.filter((role) => WEB_SUPPORTED_ROLES.includes(role));
      if (usableRoles.length === 0) {
        throw errors.validation(
          'This account has employee access only. Use the MicaraHR mobile app to view your own records.',
        );
      }

      const session = toSession(user);
      preferences.write(SESSION_KEY, session);

      await recordAudit({
        actor: { userId: session.userId, name: session.displayName, roles: session.roles },
        module: 'auth',
        action: 'session.signed_in',
        summary: `${session.displayName} signed in`,
        recordType: 'session',
        recordId: session.userId,
        recordLabel: session.email,
        severity: 'info',
      });

      return session;
    });
  },

  async signOut(session: Session | null): Promise<void> {
    preferences.remove(SESSION_KEY);
    if (session) {
      await recordAudit({
        actor: { userId: session.userId, name: session.displayName, roles: session.roles },
        module: 'auth',
        action: 'session.signed_out',
        summary: `${session.displayName} signed out`,
        recordType: 'session',
        recordId: session.userId,
        recordLabel: session.email,
      });
    }
  },

  /** Switch demo account without re-typing credentials. Development only. */
  async switchTo(email: string): Promise<Session> {
    return authService.signIn(email, DEMO_PASSWORD);
  },

  demoAccounts() {
    return DEMO_CREDENTIALS;
  },
};

/**
 * The current session, for services that need to know who is calling.
 *
 * Added when compensation stopped taking `permissions` and an `AuditActor` as
 * arguments: those were the prototype asking a component for things a server
 * already knows, and every call site was one forgotten argument away from an
 * unenforced permission check.
 */
export function requireSession(): Session {
  const session = preferences.read<Session | null>(SESSION_KEY, null);
  if (!session) throw errors.validation('You are not signed in.');
  return session;
}
